import { useEffect, useRef, useState, useCallback } from "react";
import { webSocketService, WebSocketMessage, WebSocketMessageSentResponse } from "../services/websocketService";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";

interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onMessageSent?: (response: WebSocketMessageSentResponse) => void;
  onNotification?: (notification: any) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const { user, token } = useSelector((state: RootState) => ({
    user: state.auth.user,
    token: state.auth.token,
  }));

  // Initialize state by checking actual WebSocket service status (lazy initialization)
  const [isConnected, setIsConnected] = useState(() => webSocketService.getConnectionStatus());
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAuthError, setHasAuthError] = useState(false);

  const optionsRef = useRef(options);
  const isConnectedRef = useRef(webSocketService.getConnectionStatus());
  const isConnectingRef = useRef(false);
  const hasAuthErrorRef = useRef(false);
  const subscriptionIdRef = useRef<string | null>(null);

  // Helper function to check if error is authentication related
  const isAuthError = (error: any): boolean => {
    const errorMessage = error?.message || error?.description || error?.toString() || "";
    const authErrorKeywords = [
      "invalid token",
      "expired token", 
      "unauthorized",
      "authentication failed",
      "invalid or expired token",
      "token expired",
      "jwt expired",
      "jwt malformed",
      "invalid signature"
    ];
    
    return authErrorKeywords.some(keyword => 
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );
  };

  // Update options ref when options change
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // Always register callbacks on mount (whether connecting or already connected)
  useEffect(() => {
    if (!user?.id || !token) {
      return;
    }
    
    // Register callbacks - works whether connecting new or already connected
    webSocketService.connect({
      userId: user.id,
      token,
      onMessage: (message) => {
        // Always use optionsRef.current to get the latest callback
        if (optionsRef.current.onMessage) {
          optionsRef.current.onMessage(message);
        }
      },
      onMessageSent: (response) => {
        // Always use optionsRef.current to get the latest callback
        if (optionsRef.current.onMessageSent) {
          optionsRef.current.onMessageSent(response);
        }
      },
      onNotification: (notification) => {
        // Always use optionsRef.current to get the latest callback
        if (optionsRef.current.onNotification) {
          optionsRef.current.onNotification(notification);
        }
      },
      onConnect: () => {
        isConnectedRef.current = true;
        setIsConnected(true);
        setError(null);
        // Always use optionsRef.current to get the latest callback
        if (optionsRef.current.onConnect) {
          optionsRef.current.onConnect();
        }
      },
      onDisconnect: () => {
        isConnectedRef.current = false;
        setIsConnected(false);
        // Always use optionsRef.current to get the latest callback
        if (optionsRef.current.onDisconnect) {
          optionsRef.current.onDisconnect();
        }
      },
      onError: (error) => {
        setError(error.message || "WebSocket connection error");
        // Always use optionsRef.current to get the latest callback
        if (optionsRef.current.onError) {
          optionsRef.current.onError(error);
        }
      },
    }).then((subId) => {
      subscriptionIdRef.current = subId;
      
      // Sync connection state
      const actualStatus = webSocketService.getConnectionStatus();
      if (actualStatus) {
        isConnectedRef.current = true;
        setIsConnected(true);
      }
    }).catch((error) => {
      console.error("Failed to register WebSocket callbacks:", error);
    });

    // Cleanup function to remove callbacks when dependencies change
    return () => {
      if (subscriptionIdRef.current) {
        webSocketService.removeCallbacksById(subscriptionIdRef.current);
      }
    };
  }, [user?.id, token]); // Depend on user and token

  const connect = useCallback(async () => {
    if (!user?.id || !token) {
      return;
    }

    if (isConnectedRef.current || isConnectingRef.current) {
      return;
    }

    if (hasAuthErrorRef.current) {
      return;
    }

    try {
      isConnectingRef.current = true;
      setIsConnecting(true);
      setError(null);

      const subscriptionId = await webSocketService.connect({
        userId: user.id,
        token,
        onMessage: (message) => {
          // Always call the current onMessage callback using optionsRef
          if (optionsRef.current.onMessage) {
            optionsRef.current.onMessage(message);
          }
        },
        onMessageSent: (response) => {
          console.log("✅ Message sent confirmation:", response);
          optionsRef.current.onMessageSent?.(response);
        },
        onNotification: (notification) => {
          console.log("🔔 Notification received in hook:", notification);
          if (optionsRef.current.onNotification) {
            optionsRef.current.onNotification(notification);
          }
        },
        onConnect: () => {
          isConnectedRef.current = true;
          setIsConnected(true);
          setError(null);
          if (optionsRef.current.onConnect) {
            optionsRef.current.onConnect();
          }
        },
        onDisconnect: () => {
          isConnectedRef.current = false;
          setIsConnected(false);
          if (optionsRef.current.onDisconnect) {
            optionsRef.current.onDisconnect();
          }
        },
        onError: (error) => {
          setError(error.message || "WebSocket connection error");
          if (optionsRef.current.onError) {
            optionsRef.current.onError(error);
          }
        },
      });

      // Store subscription ID for cleanup
      subscriptionIdRef.current = subscriptionId;

      // Join user room after connection
      webSocketService.joinUserRoom(user.id);

    } catch (error: any) {
      // Check if it's an authentication error
      if (isAuthError(error)) {
        hasAuthErrorRef.current = true;
        setHasAuthError(true);
      }
      
      setError(error.message || "Failed to connect to WebSocket");
      if (optionsRef.current.onError) {
        optionsRef.current.onError(error);
      }
    } finally {
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  }, [user?.id, token]);

  const disconnect = useCallback(() => {
    webSocketService.disconnect();
    isConnectedRef.current = false;
    isConnectingRef.current = false;
    hasAuthErrorRef.current = false;
    setIsConnected(false);
    setIsConnecting(false);
    setHasAuthError(false);
    setError(null);
  }, []);

  const sendMessage = useCallback(async (messageData: {
    receivers: string[];
    sender: string;
    type: string;
    detail?: any;
    conversationId?: string;
  }) => {
    try {
      const response = await webSocketService.sendMessage(messageData);
      return response;
    } catch (error: any) {
      setError(error.message || "Failed to send message");
      throw error;
    }
  }, []);

  // Sync connection state periodically
  useEffect(() => {
    const syncInterval = setInterval(() => {
      const actualConnectionStatus = webSocketService.getConnectionStatus();
      if (actualConnectionStatus !== isConnectedRef.current) {
        isConnectedRef.current = actualConnectionStatus;
        setIsConnected(actualConnectionStatus);
      }
    }, 1000); // Sync every second

    return () => clearInterval(syncInterval);
  }, []);

  // Cleanup on unmount (only run once when component unmounts)
  useEffect(() => {
    return () => {
      // Remove callbacks by subscription ID to prevent memory leaks
      if (subscriptionIdRef.current) {
        webSocketService.removeCallbacksById(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
      // Note: We don't call disconnect() here to allow WebSocket to persist across component remounts
      // disconnect();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  return {
    isConnected,
    isConnecting,
    error,
    hasAuthError,
    connect,
    disconnect,
    sendMessage,
  };
};





